import { useEffect, useState } from 'react';
import { Plus, Copy, Pause, Play, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { formatBRL } from '../../lib/currency';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { StatusBadge, STATUS_COUPON } from '../../components/admin/StatusBadge';

interface Coupon {
  id: string; code: string; discount_percent: number;
  max_redemptions: number | null; redemptions_count: number;
  expires_at: string | null; active: boolean; scope: string | null;
  scope_user_email: string | null;
}

// No 0/O/1/I to avoid confusion
const SAFE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
function generateCode(len = 10): string {
  return Array.from({ length: len }, () => SAFE_CHARS[Math.floor(Math.random() * SAFE_CHARS.length)]).join('');
}

function CouponPreview({ discount, code }: { discount: number; code: string }) {
  if (!code) return null;
  const examplePrice = 24.90;
  const newPrice = examplePrice * (1 - discount / 100);
  return (
    <div style={{ padding: '0.75rem', backgroundColor: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-caption)' }}>
      <p style={{ color: '#4ade80', margin: 0 }}>
        ✓ Cupom <strong style={{ fontFamily: 'monospace' }}>{code}</strong> aplicado, {discount}% off
      </p>
      <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
        Plano Mensal: <s>R$ {examplePrice.toFixed(2)}</s> → <strong>R$ {newPrice.toFixed(2)}</strong>
      </p>
    </div>
  );
}

function getCouponStatusKey(c: Coupon): keyof typeof STATUS_COUPON {
  if (c.active) return 'active';
  if (c.expires_at && new Date(c.expires_at) < new Date()) return 'expired';
  return 'paused';
}

export function AdminCouponsPage() {
  const { profile }     = useAuth();
  const [coupons,  setCoupons]  = useState<Coupon[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Form state
  const [code,          setCode]          = useState('');
  const [discount,      setDiscount]      = useState('');
  const [maxUses,       setMaxUses]       = useState('');
  const [expires,       setExpires]       = useState('');
  const [userEmail,     setUserEmail]     = useState('');
  const [campaign,      setCampaign]      = useState('');
  const [saving,        setSaving]        = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    const { data } = await supabase.from('discount_coupons').select('*').order('created_at', { ascending: false });
    if (data) setCoupons(data);
    setLoading(false);
  }

  async function save() {
    if (!(code ?? '').trim() || !discount) return;
    setSaving(true);
    const disc = parseInt(discount);
    if (disc < 1 || disc > 100) { setSaving(false); return; }

    const payload: Record<string, unknown> = {
      code: (code ?? '').trim().toUpperCase(),
      discount_percent: disc,
      max_redemptions: maxUses ? parseInt(maxUses) : null,
      expires_at: expires || null,
      active: true,
      redemptions_count: 0,
      scope_user_email: userEmail.trim().toLowerCase() || null,
    };

    // Include campaign_name gracefully, column may not exist yet
    if (campaign.trim()) {
      payload.campaign_name = campaign.trim();
    }

    await supabase.from('discount_coupons').insert(payload);
    await supabase.from('admin_audit_log').insert({
      admin_id: profile?.id, action: 'create_coupon',
      entity_type: 'coupon', entity_id: code,
      new_value: { code, discount_percent: disc, campaign_name: campaign.trim() || null },
    });
    setSaving(false);
    setShowForm(false);
    setCode(''); setDiscount(''); setMaxUses(''); setExpires(''); setUserEmail(''); setCampaign('');
    load();
  }

  async function toggleActive(c: Coupon) {
    await supabase.from('discount_coupons').update({ active: !c.active }).eq('id', c.id);
    await supabase.from('admin_audit_log').insert({
      admin_id: profile?.id, action: c.active ? 'pause_coupon' : 'activate_coupon',
      entity_type: 'coupon', entity_id: c.id,
    });
    load();
  }

  async function deleteCoupon(id: string) {
    await supabase.from('discount_coupons').delete().eq('id', id);
    await supabase.from('admin_audit_log').insert({
      admin_id: profile?.id, action: 'delete_coupon', entity_type: 'coupon', entity_id: id,
    });
    setDeleteId(null);
    load();
  }

  function duplicateIntoForm(c: Coupon) {
    setCode(generateCode()); setDiscount(String(c.discount_percent));
    setMaxUses(c.max_redemptions ? String(c.max_redemptions) : '');
    setExpires(c.expires_at ? c.expires_at.slice(0, 10) : '');
    setCampaign('');
    setShowForm(true);
  }

  return (
    <div>
      <AdminPageHeader
        title="Cupons de Desconto"
        objective="Crie e gerencie códigos de desconto para campanhas comerciais. Cupons podem ser públicos (qualquer usuário) ou privados (vinculados a um e-mail específico). Cupons expirados são exibidos em cinza."
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div />
        <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(v => !v); setCode(generateCode()); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Plus size={16} /> Novo cupom
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card animate-fade-in" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 'var(--text-label)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1rem' }}>Criar cupom</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Código</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input type="text" className="form-input" value={code} onChange={e => setCode(e.target.value.toUpperCase())} style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
                <button className="btn btn-ghost btn-sm" onClick={() => setCode(generateCode())} title="Gerar automaticamente">
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Desconto %</label>
              <input type="number" className="form-input" value={discount} onChange={e => setDiscount(e.target.value)} min="1" max="100" placeholder="20" />
            </div>
            <div className="form-group">
              <label className="form-label">Máx. usos</label>
              <input type="number" className="form-input" value={maxUses} onChange={e => setMaxUses(e.target.value)} placeholder="Ilimitado" />
            </div>
            <div className="form-group">
              <label className="form-label">Expira em</label>
              <input type="date" className="form-input" value={expires} onChange={e => setExpires(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">
                Usuário específico
                <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.35rem' }}>(opcional)</span>
              </label>
              <input
                type="email"
                className="form-input"
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
                placeholder="usuario@email.com"
              />
              <p className="form-hint">Deixe em branco para cupom público (todos os usuários)</p>
            </div>
            <div className="form-group">
              <label className="form-label">
                Nome da campanha / origem
                <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.35rem' }}>(opcional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={campaign}
                onChange={e => setCampaign(e.target.value)}
                placeholder="Ex: Black Friday, Influencer X, Email VIP"
              />
            </div>
          </div>

          <CouponPreview discount={parseInt(discount) || 0} code={code} />

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !code || !discount}>
              {saving ? 'Salvando...' : 'Criar cupom'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Coupon table */}
      {loading ? (
        <div className="card shimmer" style={{ height: '200px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Código</th>
                <th>Desconto</th>
                <th>Usos</th>
                <th>Expira</th>
                <th>Usuário</th>
                <th>Status</th>
                <th>Impacto</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c.id}>
                  <td><code style={{ backgroundColor: 'rgba(31,78,95,0.12)', color: 'var(--petrol)', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}>{c.code}</code></td>
                  <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.discount_percent}%</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>{c.redemptions_count}/{c.max_redemptions ?? '∞'}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{c.expires_at ? new Date(c.expires_at).toLocaleDateString('pt-BR') : '-'}</td>
                  <td style={{ fontSize: 'var(--text-caption)', color: c.scope_user_email ? 'var(--petrol)' : 'var(--text-muted)' }}>
                    {c.scope_user_email ?? <span style={{ opacity: 0.5 }}>Público</span>}
                  </td>
                  <td>
                    <StatusBadge {...STATUS_COUPON[getCouponStatusKey(c)]} />
                  </td>
                  <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                    {c.redemptions_count > 0
                      ? <><span style={{ color: '#fbbf24', fontWeight: 600 }}>{formatBRL(c.redemptions_count * (c.discount_percent / 100) * 24.90)}</span> <span style={{ color: 'var(--text-muted)' }}>Est.</span></>
                      : '-'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => duplicateIntoForm(c)} title="Duplicar"><Copy size={13} /></button>
                      <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(c)} title={c.active ? 'Pausar' : 'Ativar'}>
                        {c.active ? <Pause size={13} /> : <Play size={13} />}
                      </button>
                      {deleteId === c.id ? (
                        <>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteCoupon(c.id)}>Confirmar</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDeleteId(null)}>Não</button>
                        </>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => setDeleteId(c.id)} title="Excluir"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>Nenhum cupom ainda</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
